import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { db } from '../lib/firebase.js'
import { onValue, ref } from 'firebase/database'
import { addRegistration } from '../lib/data.js'
import CLUB from '../config/club.js'
import PageNav from '../components/PageNav.jsx'
import {
  calcCourseHandicap, calcPlayingHandicap,
  normalizeGhin, isValidGhin, DEFAULT_ALLOWANCE,
} from '../lib/handicap.js'
import { lookupGhinHandicap } from '../lib/ghinLookup.js'
import { buildHandicapEntry, resolveAllowSelfReported, isSelfReportedBlocked } from '../lib/handicapEntry.js'

const PBCC_GREEN = CLUB.primaryColor
const PBCC_GOLD = CLUB.accentColor

export default function RegisterApp() {
  const [params] = useSearchParams()
  const tournamentId = params.get('t')
  const [tournament, setTournament] = useState(null)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!tournamentId) {
      setError('Missing tournament ID. Scan the registration QR code from the pro shop.')
      return
    }
    const off = onValue(ref(db, `tournaments/${tournamentId}`), (snap) => {
      const v = snap.val()
      if (!v) setError('Tournament not found.')
      else setTournament(v)
    })
    return () => off()
  }, [tournamentId])

  if (error) return <Centered><Card>{error}</Card></Centered>
  if (!tournament) return <Centered>Loading…</Centered>

  const info = tournament.info || {}
  const regs = tournament.registrations || {}
  const registeredCount = Object.keys(regs).length
  const max = Number(info.maxTeams) || null
  const status = info.registrationStatus || 'open'
  const deadline = info.entryDeadline ? new Date(info.entryDeadline) : null
  const deadlinePassed = deadline ? new Date() > new Date(deadline.getTime() + 24 * 60 * 60 * 1000) : false
  const full = max && registeredCount >= max
  const closed = status === 'closed' || deadlinePassed || full

  return (
    <div className="min-h-full relative" style={{ backgroundColor: PBCC_GREEN }}>
      <PageNav back />
      <div className="max-w-md mx-auto p-6 pt-12">
        <header className="text-center mb-6">
          <img src={CLUB.logo} alt="" className="h-20 mx-auto mb-3 bg-white rounded-2xl p-1.5" />
          <p className="text-sm uppercase tracking-widest" style={{ color: PBCC_GOLD }}>{info.course || 'Tournament Registration'}</p>
          <h1 className="text-2xl font-extrabold text-white mt-1">{info.name}</h1>
          <p className="text-sm text-white/70">{info.date} · {info.format || '4-Ball Best Ball'}</p>
        </header>

        <div className="rounded-xl px-4 py-3 mb-4 flex justify-between items-center"
             style={{ backgroundColor: 'rgba(0,0,0,0.25)', border: `1px solid ${PBCC_GOLD}40` }}>
          <span className="text-sm uppercase tracking-widest text-white/70">Registered</span>
          <span className="font-extrabold" style={{ color: PBCC_GOLD }}>
            {registeredCount}{max ? ` of ${max}` : ''}
          </span>
        </div>

        {closed
          ? <ClosedCard status={status} full={full} deadlinePassed={deadlinePassed} />
          : <RegisterForm tournamentId={tournamentId} info={info} />
        }
      </div>
    </div>
  )
}

function ClosedCard({ status, full, deadlinePassed }) {
  let msg = 'Registration is closed.'
  if (full) msg = 'Registration is full.'
  else if (deadlinePassed) msg = 'Registration deadline has passed.'
  else if (status === 'waitlist') msg = 'Registration is full — joining the waitlist below.'
  return (
    <Card>
      <p className="text-center font-bold text-white text-lg">{msg}</p>
      <p className="text-center text-sm text-white/70 mt-2">
        Contact the tournament committee if you believe this is an error.
      </p>
    </Card>
  )
}

function RegisterForm({ tournamentId, info }) {
  // Tournament-level handicap settings drive what extra fields render
  // per player. Defaults keep the form behaving exactly as before for
  // tournaments that never set these (mode === 'none').
  const handicapMode = info?.handicapMode || 'none'
  const slopeRating = Number(info?.slopeRating) || 113
  // Course rating + par feed the full USGA WHS formula. Older
  // tournaments that pre-date the course-search feature won't have
  // them set — calcCourseHandicap silently falls back to the slope-
  // only form in that case.
  const courseRating = Number(info?.courseRating) || null
  const coursePar = Number(info?.coursePar) || null
  const allowance = Number.isFinite(Number(info?.handicapAllowance))
    ? Number(info.handicapAllowance) : DEFAULT_ALLOWANCE
  const showHandicap = handicapMode !== 'none'
  // When false, only a GHIN-verified index is accepted (manual field hidden,
  // submit blocked unless every player verified). Default: required → false.
  const allowSelfReported = resolveAllowSelfReported(info)

  // Players per team comes from the event's registration settings. Defaults
  // to 2 (the 4-Ball pair) so events created before this setting existed keep
  // behaving exactly as before. 1 = individual entry.
  const teamSize = Math.max(1, Math.min(4, Number(info?.teamSize) || 2))

  const [teamName, setTeamName] = useState('')
  // Parallel per-player arrays, one slot per team member. Kept as strings so
  // inputs stay controlled while empty.
  const [names, setNames] = useState(() => Array(teamSize).fill(''))
  const [ghins, setGhins] = useState(() => Array(teamSize).fill(''))
  const [indexes, setIndexes] = useState(() => Array(teamSize).fill(''))
  // Per-player handicap SOURCE: 'ghin' once a GHIN lookup fills the index,
  // 'self' when it's typed by hand. Defaults 'self' (a bare index is
  // self-reported until a lookup verifies it).
  const [sources, setSources] = useState(() => Array(teamSize).fill('self'))
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)
  const [err, setErr] = useState('')

  const setAt = (setter) => (i, v) => setter((arr) => arr.map((x, k) => (k === i ? v : x)))
  const setNameAt = setAt(setNames)
  const setGhinAt = setAt(setGhins)
  const setIndexAt = setAt(setIndexes)
  const setSourceAt = setAt(setSources)

  const submit = async (e) => {
    e.preventDefault()
    setErr('')
    if (names.some((n) => !n.trim())) {
      setErr(teamSize === 1 ? 'Player name is required.' : 'All player names are required.')
      return
    }
    if (showHandicap && !allowSelfReported) {
      // GHIN-verified only: every player must have completed a successful GHIN
      // lookup (source 'ghin') with a resolved index. Self-reported is blocked.
      for (let i = 0; i < teamSize; i++) {
        if (indexes[i] === '' || isSelfReportedBlocked(sources[i], allowSelfReported)) {
          setErr(`Player ${i + 1}: this event requires a GHIN-verified handicap — enter a valid Handicap (GH) ID so it can be verified.`)
          return
        }
      }
    } else if (showHandicap) {
      // Self-reported allowed — either a GHIN OR a manual index per player.
      for (let i = 0; i < teamSize; i++) {
        if (!isValidGhin(ghins[i]) && indexes[i] === '') {
          setErr(`Player ${i + 1} needs a Handicap (GH) ID or a Handicap Index.`)
          return
        }
      }
    }
    setSubmitting(true)
    try {
      const cleanNames = names.map((n) => n.trim())
      const reg = {
        teamName: teamName.trim() || cleanNames.map((n) => n.split(' ')[0]).join(' / '),
        players: cleanNames,
        email: email.trim() || null,
        phone: phone.trim() || null,
      }
      if (showHandicap) {
        // Parallel array — indexes line up with reg.players. Each entry carries
        // its SOURCE flag ('ghin' | 'self') captured at entry.
        reg.playerHandicaps = cleanNames.map((_, i) => buildHandicapEntry({
          ghin: ghins[i], index: indexes[i], source: sources[i],
          slopeRating, courseRating, coursePar, allowance,
        }))
      }
      await addRegistration(tournamentId, reg)
      setDone(true)
    } catch (e) {
      setErr(e?.message || String(e))
    } finally {
      setSubmitting(false)
    }
  }

  if (done) {
    return (
      <Card>
        <div className="text-5xl text-center mb-2">✓</div>
        <p className="text-center font-extrabold text-white text-lg">You're registered!</p>
        <p className="text-center text-sm text-white/75 mt-2 leading-relaxed">
          Look for the tournament QR code at the pro shop on tournament day.
        </p>
      </Card>
    )
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      <Field label={teamSize === 1 ? 'Display Name' : 'Team Name'}>
        <input className="input" value={teamName} onChange={(e) => setTeamName(e.target.value)} placeholder="Auto-generated if blank" />
      </Field>
      {names.map((nm, i) => (
        <PlayerBlock
          key={i}
          idx={i + 1}
          name={nm} setName={(v) => setNameAt(i, v)}
          ghin={ghins[i]} setGhin={(v) => setGhinAt(i, v)}
          index={indexes[i]} setIndex={(v) => setIndexAt(i, v)}
          setSource={(v) => setSourceAt(i, v)}
          handicapMode={handicapMode}
          allowSelfReported={allowSelfReported}
          slopeRating={slopeRating}
          courseRating={courseRating}
          coursePar={coursePar}
          allowance={allowance}
          autoFocus={i === 0} />
      ))}
      <Field label="Email">
        <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" autoCapitalize="none" />
      </Field>
      <Field label="Phone">
        <input className="input" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="555-0123" />
      </Field>
      {err ? <p className="text-red-300 text-sm">{err}</p> : null}
      <button
        type="submit"
        disabled={submitting}
        className="w-full py-4 rounded-2xl font-extrabold text-base uppercase tracking-widest transition active:scale-[0.98] disabled:opacity-50"
        style={{
          background: `linear-gradient(180deg, ${PBCC_GOLD} 0%, #123D26 100%)`,
          color: PBCC_GREEN,
          boxShadow: '0 6px 18px rgba(18, 61, 38,0.4)',
        }}>
        {submitting ? 'Registering…' : 'Register Now'}
      </button>
    </form>
  )
}

function PlayerBlock({
  idx, name, setName, ghin, setGhin, index, setIndex, setSource,
  handicapMode, allowSelfReported = true, slopeRating, courseRating, coursePar, allowance, autoFocus,
}) {
  const ghinRequired = handicapMode === 'required'
  const ghinOptional = handicapMode === 'optional'
  const showHandicap = handicapMode !== 'none'

  // Live GHIN lookup status per player block. Values:
  //   'idle'     — nothing tried yet
  //   'loading'  — POST /golfer_login + GET /golfers in flight
  //   'ok'       — handicap-index pulled successfully; manual field hidden
  //   'notfound' — GHIN didn't resolve to a golfer
  //   'auth'     — couldn't authenticate against api.ghin.com
  //   'network'  — fetch threw / non-OK HTTP
  const [ghinStatus, setGhinStatus] = useState('idle')
  const runGhinLookup = async () => {
    const clean = (ghin || '').replace(/\D/g, '')
    if (clean.length < 7) {
      // Too short to be a real GHIN — clear any prior "ok" so the
      // manual Index field comes back into view.
      setGhinStatus(clean.length === 0 ? 'idle' : 'notfound')
      return
    }
    setGhinStatus('loading')
    const res = await lookupGhinHandicap(clean)
    if (res.ok) {
      setIndex(String(res.handicapIndex))
      setSource?.('ghin') // verified from the GHIN service
      // Auto-fill the player's name from the GHIN record. Only overwrite
      // when GHIN actually returned a non-empty fullName — keeps any
      // teammate name the player already typed if the API didn't carry
      // a name for this golfer (rare but possible).
      if (res.fullName) setName(res.fullName)
      setGhinStatus('ok')
    } else {
      setGhinStatus(res.reason || 'notfound')
    }
  }

  // Manual Handicap Index field stays visible until a GHIN lookup
  // actually succeeds. This replaces the old "for now the player still
  // types it in" placeholder behaviour referenced by the previous
  // comment at this position.
  // Manual index only when self-reporting is allowed; otherwise a GHIN lookup
  // is the only way to set it.
  const showIndex = showHandicap && ghinStatus !== 'ok' && allowSelfReported
  const idxNum = index === '' ? null : Number(index)
  const ch = idxNum == null ? null : calcCourseHandicap(idxNum, slopeRating, courseRating, coursePar)
  const ph = ch == null ? null : calcPlayingHandicap(ch, allowance)
  return (
    <div className="space-y-3">
      <Field label={`Player ${idx} *`}>
        <input className="input" autoFocus={autoFocus} value={name}
          onChange={(e) => setName(e.target.value)} placeholder="First Last" />
      </Field>
      {showHandicap ? (
        <>
          <Field label={`Handicap (GH) ID${ghinRequired ? ' *' : ''}`}>
            <input
              className="input"
              type="text"
              inputMode="numeric"
              value={ghin}
              onChange={(e) => {
                setGhin(e.target.value)
                // Typing invalidates whatever we last looked up.
                if (ghinStatus !== 'idle') setGhinStatus('idle')
              }}
              onBlur={runGhinLookup}
              placeholder="e.g. 1234567"
              maxLength={10} />
          </Field>
          <GhinStatusLine status={ghinStatus} index={index} />
          {showIndex ? (
            <Field label={`Handicap Index${ghinOptional ? ' (if no Handicap (GH) ID)' : ' *'}`}>
              <input
                className="input"
                type="number"
                step="0.1"
                value={index}
                onChange={(e) => { setIndex(e.target.value); setSource?.('self') }}
                placeholder="e.g. 12.4" />
            </Field>
          ) : null}
          {ch != null ? (
            <div className="rounded-xl px-4 py-3 flex justify-between text-sm"
                 style={{ backgroundColor: 'rgba(0,0,0,0.25)', border: `1px solid ${PBCC_GOLD}40`, color: '#fff' }}>
              <span className="uppercase tracking-widest text-white/70 text-sm">
                Course HCP / Playing HCP
              </span>
              <span className="font-extrabold" style={{ color: PBCC_GOLD }}>{ch} / {ph}</span>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  )
}

function Card({ children }) {
  return (
    <div className="rounded-2xl p-5"
         style={{ backgroundColor: 'rgba(0,0,0,0.25)', border: `1px solid ${PBCC_GOLD}40` }}>
      {children}
    </div>
  )
}

// Per-row status under the GHIN field. Dark-theme styling matched to
// the rest of the public registration form. `index` is shown alongside
// the success message so the player can see what we filled in.
function GhinStatusLine({ status, index }) {
  if (status === 'loading') {
    return (
      <p className="text-sm text-white/80" role="status">
        Looking up handicap…
      </p>
    )
  }
  if (status === 'ok') {
    return (
      <p className="text-sm font-bold" style={{ color: PBCC_GOLD }} role="status">
        ✓ Handicap Index pulled from handicap lookup: <span className="font-extrabold">{index}</span>
      </p>
    )
  }
  if (status === 'notfound') {
    return (
      <p className="text-sm text-red-300" role="status">
        Handicap number not found — enter your Handicap Index manually below.
      </p>
    )
  }
  if (status === 'auth' || status === 'network') {
    return (
      <p className="text-sm text-red-300" role="status">
        Couldn't reach the handicap lookup service — enter your Handicap Index manually below.
      </p>
    )
  }
  return null
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-sm uppercase tracking-widest font-bold mb-1 block" style={{ color: PBCC_GOLD }}>{label}</span>
      {children}
      <style>{`
        .input {
          width: 100%;
          background: rgba(255,255,255,0.95);
          color: #111;
          border-radius: 12px;
          padding: 12px 14px;
          font-size: 16px;
          border: 1px solid rgba(255,255,255,0.3);
        }
        .input:focus { outline: 2px solid ${PBCC_GOLD}; outline-offset: 1px; }
      `}</style>
    </label>
  )
}

function Centered({ children }) {
  return (
    <div className="min-h-full flex items-center justify-center p-6" style={{ backgroundColor: PBCC_GREEN, color: PBCC_GOLD }}>
      {children}
    </div>
  )
}
